import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import client from "../lib/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Bell, Heart, Sparkles, Crown, Check, ArrowRight, HeartCrack } from "lucide-react";
import { toast } from "sonner";

const TYPE_META = {
  elite_match:    { icon: Sparkles,   color: "text-[#C5A880]", bg: "bg-[#C5A880]/10" },
  new_like:       { icon: Heart,      color: "text-[#C5A880]", bg: "bg-[#C5A880]/10" },
  mutual_match:   { icon: Crown,      color: "text-[#C5A880]", bg: "bg-[#C5A880]/10" },
  like_cancelled: { icon: HeartCrack, color: "text-[#A29F9B]", bg: "bg-white/5" },
};

const linkFor = (n) => {
  if (n.type === "elite_match" && n.payload?.profile_id) return `/profile/${n.payload.profile_id}`;
  if (n.type === "mutual_match" && n.payload?.match_id) return `/chat/${n.payload.match_id}`;
  if (n.type === "new_like") return `/admiradores`;
  if (n.type === "like_cancelled") return `/discover`;
  return "/discover";
};

const Notifications = () => {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const [n, p] = await Promise.all([
        client.get("/notifications"),
        client.get("/notifications/preferences"),
      ]);
      setItems(n.data.notifications);
      setUnread(n.data.unread);
      setPrefs(p.data.preferences);
    } catch (err) {
      // Notifications endpoint is best-effort: render an empty list rather
      // than a noisy banner. We log so failures show up in DevTools.
      // eslint-disable-next-line no-console
      console.warn("Failed to load notifications:", err);
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const markRead = async (id) => {
    await client.post(`/notifications/${id}/read`);
    setItems((arr) => arr.map(x => x.id === id ? { ...x, read: true } : x));
    setUnread((u) => Math.max(0, u - 1));
  };

  const markAllRead = async () => {
    await client.post("/notifications/read-all");
    setItems((arr) => arr.map(x => ({ ...x, read: true })));
    setUnread(0);
    toast.success("Todas marcadas como lidas.");
  };

  const togglePref = (key) => {
    setPrefs((prev) => {
      const nextVal = !prev[key];
      // Fire the PUT against the freshly-computed value (avoids stale-closure race
      // when the user clicks two toggles back-to-back).
      client.put("/notifications/preferences", { [key]: nextVal })
            .catch((err) => console.warn("[notif-prefs] PUT failed", err));
      return { ...prev, [key]: nextVal };
    });
  };

  return (
    <div className="min-h-screen bg-[#0C0E12]" data-testid="notifications-page">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 md:px-12 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <span className="label-uppercase">Notificações</span>
            <h1 className="font-display text-3xl md:text-5xl mt-3">Inbox</h1>
            {unread > 0 && (
              <p className="text-sm text-[#A29F9B] mt-2" data-testid="unread-count">
                {unread} {unread === 1 ? "não lida" : "não lidas"}
              </p>
            )}
          </div>
          {unread > 0 && (
            <button onClick={markAllRead} className="btn-secondary text-sm" data-testid="mark-all-read">
              Marcar todas como lidas
            </button>
          )}
        </div>

        {loading && <div className="text-[#A29F9B]">A carregar...</div>}

        {!loading && items.length === 0 && (
          <div className="bg-[#161920] border border-white/5 rounded-2xl p-12 text-center" data-testid="empty-notifications">
            <Bell className="w-8 h-8 text-[#C5A880] mx-auto mb-4" />
            <h3 className="font-display text-2xl mb-2">Sem notificações ainda</h3>
            <p className="text-[#A29F9B]">Avisamos-te quando aparecer um match de elite, um novo like ou uma ligação mútua.</p>
          </div>
        )}

        <div className="space-y-3">
          {items.map((n) => {
            const meta = TYPE_META[n.type] || { icon: Bell, color: "text-[#DCD7D2]", bg: "bg-white/5" };
            const Icon = meta.icon;
            return (
              <div key={n.id}
                className={`bg-[#161920] border rounded-2xl p-5 flex gap-4 items-start transition-colors ${
                  n.read ? "border-white/5 opacity-70" : "border-[#C5A880]/30"
                }`}
                data-testid={`notification-${n.id}`}>
                <div className={`w-10 h-10 rounded-full ${meta.bg} flex items-center justify-center shrink-0`}>
                  <Icon className={`w-4 h-4 ${meta.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-display text-lg leading-tight">{n.title}</h3>
                    <span className="text-[10px] uppercase tracking-widest text-[#A29F9B] shrink-0">
                      {new Date(n.created_at).toLocaleDateString("pt-PT", { day: "2-digit", month: "short" })}
                    </span>
                  </div>
                  <p className="text-sm text-[#DCD7D2] mt-1 leading-relaxed">{n.body}</p>
                  <div className="mt-3 flex items-center gap-3">
                    <Link to={linkFor(n)} onClick={()=>!n.read && markRead(n.id)}
                      className="text-xs inline-flex items-center gap-1 text-[#C5A880] hover:underline"
                      data-testid={`open-${n.id}`}>
                      Abrir <ArrowRight className="w-3 h-3" />
                    </Link>
                    {!n.read && (
                      <button onClick={()=>markRead(n.id)}
                        className="text-xs text-[#A29F9B] hover:text-[#F5F2EE] inline-flex items-center gap-1"
                        data-testid={`mark-read-${n.id}`}>
                        <Check className="w-3 h-3" /> Marcar lida
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Preferences */}
        {prefs && (
          <div className="mt-14 bg-[#161920] border border-white/5 rounded-2xl p-8" data-testid="prefs-section">
            <span className="label-uppercase">Preferências</span>
            <h2 className="font-display text-2xl mt-3 mb-6">O que queres receber por email</h2>
            <div className="space-y-3">
              {[
                { k: "elite_match", title: "Match de elite (Top 5%)", desc: "Quando alguém de altíssima compatibilidade chega à plataforma." },
                { k: "new_like", title: "Alguém te dá like", desc: "Notificação anónima — só vês quem desbloqueando o Premium." },
                { k: "mutual_match", title: "Ligação mútua", desc: "Quando partilhas um like com alguém — começa a conversa." },
                { k: "like_cancelled", title: "Convite retirado", desc: "Aviso discreto quando um admirador cancela o convite que tinha enviado." },
                { k: "weekly_digest", title: "Resumo semanal de visitas", desc: "Email semanal com quantas pessoas viram o teu perfil." },
                { k: "renewal_reminder", title: "Lembrete de renovação Premium", desc: "Aviso amigável 3 dias antes do débito automático da subscrição." },
                { k: "new_article", title: "Novos artigos editoriais", desc: "Avisamos por email quando publicamos um artigo novo no blog." },
              ].map(({k, title, desc}) => (
                <div key={k} className="flex items-start justify-between gap-4 py-3 border-b border-white/5 last:border-0" data-testid={`pref-${k}`}>
                  <div>
                    <p className="text-[#F5F2EE] font-medium">{title}</p>
                    <p className="text-sm text-[#A29F9B] mt-1">{desc}</p>
                  </div>
                  <button onClick={()=>togglePref(k)}
                    role="switch"
                    aria-checked={!!prefs[k]}
                    aria-label={title}
                    className={`relative w-12 h-6 rounded-full transition-colors shrink-0 mt-1 ${
                      prefs[k] ? "bg-[#C5A880]" : "bg-white/10"
                    }`}
                    data-testid={`toggle-${k}`}>
                    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                      prefs[k] ? "translate-x-6" : "translate-x-0.5"
                    }`} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Notifications;
